<template>
    <section v-if="hasTitle" class="widget" :class="$mq">
        <header class="widget__header">
            <span v-if="hasIcon" class="widget__icon">
                <i :class="iconClass" aria-hidden="true"></i>
            </span>
            <span class="widget__heading">
                <span class="widget__title">{{ displayTitle }}</span>
                <span v-if="hasDescription" class="widget__description">{{ displayDescription }}</span>
            </span>
            <button v-if="showsOpenInTool" type="button" class="widget__more" @click="openTool">
                {{ openInToolLabel }}
            </button>
        </header>

        <p v-if="isEmpty" class="widget__empty">{{ emptyText }}</p>

        <div v-else class="widget__rows">
            <div v-for="(row, index) in displayedRows" :key="row.id" class="widget__row" :class="{ 'has-divider': rowHasDivider(index) }">
                <WidgetRow :title="row.title || ''" :reference="row.reference || ''" :description="row.description || ''" :show-description-dot="Boolean(row.showDescriptionDot)" :description-dot-color="row.descriptionDotColor || ''" :value="row.value || ''" :show-value-dot="Boolean(row.showValueDot)" :value-dot-color="row.valueDotColor || ''" :label="row.label || ''" :show-radio="Boolean(row.showRadio)" :show-chevron="row.showChevron !== false" :selected="selectedId === row.id" @navigate="openRow(row)" @select="selectRow(row)" />
            </div>
            <div v-if="showsExpandRow" class="widget__row">
                <button type="button" class="widget__expand" @click="expandRows">
                    <i class="i-andy-expand-vertical-fill i-andy-sm" aria-hidden="true"></i>
                    <span class="widget__expand-label">{{ expandLabel }}</span>
                </button>
            </div>
        </div>

        <footer v-if="showsFooter" class="widget__footer">
            <button v-for="(button, index) in actionButtons" :key="button.label + index" type="button" class="widget__action" :class="{ 'is-primary': index === 0 }" @click="onButtonClick">
                {{ button.label.trim() }}
            </button>
            <button v-if="showsNextButton" type="button" class="widget__action widget__next" :disabled="nextLocked" @click="onShowNext">
                {{ nextLabel }}
            </button>
        </footer>
    </section>
</template>

<script>
import WidgetRow from '@/components/domain/andy-agents/WidgetRow.vue'
import { getAndyAgentsToolTitleKey } from '@/utils/andyAgentsToolLabels.js'
import { getExpandMoreCount, getInitialDisplayedCount, getShowNextCount, hasRadioRows } from '@/utils/andyAgentsWidgetRows.js'

const BUTTON_LIMIT = 3

export default {
    name: 'Widget',
    components: { WidgetRow },
    props: {
        title: { type: String, default: '' },
        icon: { type: String, default: '' },
        description: { type: String, default: '' },
        tool: { type: String, default: '' },
        route: { type: String, default: '' },
        moreRoute: { type: String, default: '' },
        visibleRows: {
            default: undefined,
            validator(value) {
                return value === undefined || value === null || (typeof value === 'number' && !Number.isNaN(value))
            }
        },
        total: {
            default: undefined,
            validator(value) {
                return value === undefined || value === null || (typeof value === 'number' && !Number.isNaN(value))
            }
        },
        offset: { type: Number, default: 0 },
        nextCursor: { type: String, default: '' },
        exampleId: { type: String, default: '' },
        rows: { type: Array, default: () => [] },
        emptyText: { type: String, default: '' },
        buttons: { type: Array, default: () => [] }
    },
    data() {
        return {
            selectedId: null,
            displayedCount: 0,
            nextLocked: false
        }
    },
    computed: {
        hasTitle() {
            return this.hasText(this.title)
        },
        displayTitle() {
            return this.title.trim()
        },
        hasIcon() {
            return this.hasText(this.icon)
        },
        iconClass() {
            return ['i-andy-' + this.icon.trim(), 'i-andy-s']
        },
        hasDescription() {
            return this.hasText(this.description)
        },
        displayDescription() {
            return this.description.trim()
        },
        normalizedRows() {
            return Array.isArray(this.rows) ? this.rows : []
        },
        resolvedRoute() {
            if (this.hasText(this.route)) {
                return this.route.trim()
            }
            if (this.hasText(this.moreRoute)) {
                return this.moreRoute.trim()
            }
            return ''
        },
        toolLabel() {
            const key = getAndyAgentsToolTitleKey(this.tool)
            if (!key) {
                return ''
            }
            return this.$t(key)
        },
        showsOpenInTool() {
            return this.hasText(this.tool) && this.hasText(this.resolvedRoute) && this.hasText(this.toolLabel)
        },
        openInToolLabel() {
            return this.$t('andy_agents.widget.open_in_tool', { tool: this.toolLabel })
        },
        resolvedTotal() {
            const total = Number(this.total)
            if (Number.isFinite(total) && total >= 0) {
                return total
            }
            return this.offset + this.normalizedRows.length
        },
        displayedRows() {
            return this.normalizedRows.slice(0, this.displayedCount)
        },
        expandMoreCount() {
            return getExpandMoreCount({
                displayed: this.displayedCount,
                total: this.resolvedTotal,
                offset: this.offset,
                rowCount: this.normalizedRows.length
            })
        },
        showsExpandRow() {
            return this.expandMoreCount > 0
        },
        expandLabel() {
            return this.$t('andy_agents.widget.show_more', { n: this.expandMoreCount })
        },
        showNextCount() {
            return getShowNextCount({
                displayed: this.displayedCount,
                total: this.resolvedTotal,
                offset: this.offset
            })
        },
        showsNextButton() {
            return this.showNextCount > 0
        },
        nextLabel() {
            return this.$tc('andy_agents.widget.show_next', this.showNextCount, { n: this.showNextCount })
        },
        isEmpty() {
            return this.normalizedRows.length === 0
        },
        usesRadioRows() {
            return hasRadioRows(this.normalizedRows)
        },
        actionButtons() {
            if (this.isEmpty || !this.usesRadioRows) {
                return []
            }
            return (Array.isArray(this.buttons) ? this.buttons : []).filter((button) => button && this.hasText(button.label)).slice(0, BUTTON_LIMIT)
        },
        showsFooter() {
            return this.actionButtons.length > 0 || this.showsNextButton
        }
    },
    watch: {
        offset() {
            this.syncDisplayedCount()
        },
        visibleRows() {
            this.syncDisplayedCount()
        },
        rows() {
            this.syncDisplayedCount()
        }
    },
    created() {
        this.syncDisplayedCount()
    },
    methods: {
        hasText(value) {
            return typeof value === 'string' && value.trim() !== ''
        },
        // Fija cuántas filas se pintan al inicio:
        // visibleRows (5 si falta o no vale)
        // o el bloque entero si offset > 0 (porque se han mostrado ya algunas).
        syncDisplayedCount() {
            this.displayedCount = getInitialDisplayedCount({
                visibleRows: this.visibleRows,
                offset: this.offset,
                rowCount: this.normalizedRows.length
            })
            this.selectedId = null
        },
        rowHasDivider(index) {
            const isLastDataRow = index === this.displayedRows.length - 1
            if (!isLastDataRow) {
                return true
            }
            return this.showsExpandRow
        },
        expandRows() {
            if (this.expandMoreCount <= 0) {
                return
            }
            this.displayedCount += this.expandMoreCount
        },
        selectRow(row) {
            if (!row || !row.showRadio) {
                return
            }
            this.selectedId = row.id
        },
        openRow(row) {
            this.openRoute(row && row.route)
        },
        openTool() {
            this.openRoute(this.resolvedRoute)
        },
        openRoute(target) {
            if (!this.hasText(target)) {
                return
            }
            this.$router.push(target).catch((err) => {
                if (!err || err.name === 'NavigationDuplicated') {
                    return
                }
                logError(err)
            })
        },
        onShowNext() {
            if (this.nextLocked || !this.showsNextButton) {
                return
            }
            this.nextLocked = true
            this.$emit(
                'show-next',
                {
                    exampleId: this.exampleId,
                    tool: this.tool,
                    offset: this.offset + this.normalizedRows.length,
                    nextCursor: this.nextCursor,
                    total: this.resolvedTotal
                },
                (accepted) => {
                    if (!accepted) {
                        this.nextLocked = false
                    }
                }
            )
        },
        onButtonClick() {
            // La acción real de los botones queda fuera de esta tarjeta.
        }
    }
}
</script>

<style lang="scss" scoped>
.widget {
    @include border-radius(rem(4px));
    box-sizing: border-box;
    width: 100%;
    background-color: $color-white;
    border: rem(1px) solid $color-neutral-300;
    overflow: hidden;
}

.widget__header {
    @include display-flex();
    @include align-items(center);
    box-sizing: border-box;
    min-height: rem(44px);
    padding: rem(4px) rem(16px);
    gap: rem(8px);
    background-color: $neutro-t80;
    border-bottom: rem(1px) solid $color-neutral-300;
}

.widget__icon {
    @include display-flex();
    @include align-items(center);
    @include justify-content(center);
    @include border-radius(rem(4px));
    flex-shrink: 0;
    width: rem(24px);
    height: rem(24px);
    background-color: $main-t90-app;

    i {
        color: $main-text;
    }
}

.widget__heading {
    @include display-flex();
    @include align-items(center);
    flex: 0 1 auto;
    min-width: 0;
    gap: rem(8px);
}

.widget__title {
    @include font-size(s);
    @include text-ellipsis();
    flex: 0 1 auto;
    min-width: 0;
    font-family: $text-bold;
    line-height: rem(20px);
    color: $color-black;
}

.widget__description {
    @include font-size(xs);
    @include text-ellipsis();
    flex: 0 1 auto;
    min-width: 0;
    font-family: $text;
    line-height: normal;
    color: $neutro-s60;
}

.widget__more {
    @include font-size(xs);
    flex-shrink: 0;
    min-height: rem(44px);
    margin: 0 0 0 auto;
    padding: 0;
    border: 0;
    background-color: transparent;
    font-family: $text-bold;
    line-height: rem(16px);
    color: $color-secondary-500;
    white-space: nowrap;
    cursor: pointer;

    &:focus,
    &:active {
        outline: none;
        background-color: transparent;
    }
}

.widget__empty {
    @include font-size(s);
    margin: 0;
    padding: rem(24px) rem(16px);
    font-family: $text;
    line-height: rem(20px);
    text-align: center;
    color: $neutro-s60;
}

.widget__expand {
    @include display-flex();
    @include align-items(center);
    @include font-size(s);
    width: 100%;
    min-height: rem(44px);
    margin: 0;
    padding: rem(12px) rem(16px);
    border: 0;
    background-color: transparent;
    gap: rem(8px);
    text-align: left;

    cursor: pointer;

    i {
        flex-shrink: 0;
        color: $color-secondary-500;
    }

    .widget__expand-label {
        font-family: $text-bold;
        color: $main;
        line-height: rem(20px);
    }

    &:focus,
    &:active {
        outline: none;
        background-color: transparent;
    }
}

.widget__footer {
    @include display-flex();
    @include align-items(center);
    flex-wrap: wrap;
    gap: rem(8px);
    padding: rem(12px) rem(16px);
    border-top: rem(1px) solid $color-neutral-300;
}

.widget__action {
    @include font-size(s);
    @include border-radius(rem(4px));
    min-height: rem(36px);
    margin: 0;
    padding: rem(8px) rem(12px);
    border: rem(1px) solid $color-neutral-300;
    background-color: $color-white;
    font-family: $text-bold;
    line-height: rem(20px);
    color: $color-secondary-500;
    cursor: pointer;

    &:focus,
    &:active {
        outline: none;
    }

    &.is-primary {
        border-color: $color-secondary-500;
        background-color: $color-secondary-500;
        color: $color-white;
    }
}

.widget__next {
    background-color: $main-t90;
    border-color: $main-t90;
}

.widget.portrait .widget__next {
    width: 100%;
}

.widget__next:disabled {
    cursor: default;
    opacity: 0.45;
}

.widget__row.has-divider {
    border-bottom: rem(1px) solid $color-neutral-300;
}
</style>
